/**
 * The backend stamps CreatedBy / UpdatedBy with -1 when no admin was in the request:
 * Hangfire jobs, payment webhooks, customer-facing flows, startup initializers.
 * No user account has that id, so the names come back empty — we label it "System".
 */
export const SYSTEM_USER_ID = -1;
const SYSTEM_USER_LABEL = "System";

export type AuthorKind = "user" | "system" | "unknown";

export interface ResolvedAuthor {
  label: string;
  initial: string;
  kind: AuthorKind;
}

/**
 * Three states come back from the API:
 *  - id -1 with empty names: a job, webhook or customer flow — "System";
 *  - a positive id with names: a live admin account;
 *  - a positive id with empty names: the admin account was removed since (accounts are
 *    hard-deleted), so only the id is left — "Unknown user #id".
 * A missing or zero id means the row predates audit stamping and there is nothing to show.
 * @param id - the CreatedBy / UpdatedBy id from the API
 * @param firstName - resolved first name, empty when no account matched
 * @param lastName - resolved last name, empty when no account matched
 * @returns what to render, or null when there is nothing to show
 */
export const resolveAuthor = (
  id: number | undefined,
  firstName: string,
  lastName: string
): ResolvedAuthor | null => {
  if (id === SYSTEM_USER_ID) {
    return { label: SYSTEM_USER_LABEL, initial: SYSTEM_USER_LABEL.charAt(0), kind: "system" };
  }
  const label = typeof firstName === "string" ? `${firstName} ${lastName ?? ""}`.trim() : "";
  if (label) return { label, initial: firstName.charAt(0), kind: "user" };
  if (typeof id === "number" && id > 0) {
    return { label: `Unknown user #${id}`, initial: "?", kind: "unknown" };
  }
  return null;
};

export const BADGE_COLOR: Record<AuthorKind, string> = {
  user: "bg-gray-700 text-white",
  system: "bg-gray-400 text-white",
  unknown: "bg-gray-300 text-gray-700"
};

/**
 * Display name for an audit stamp, for pages that print it as inline text instead of a badge.
 * @param id - the CreatedBy / UpdatedBy id; -1 means the platform itself
 * @param firstName - resolved first name, empty when no user matched
 * @param lastName - resolved last name, empty when no user matched
 * @returns "System", the trimmed full name, or null when there is nothing to show
 */
export const formatAuditAuthorName = (
  id: number | undefined,
  firstName: string,
  lastName: string
): string | null => resolveAuthor(id, firstName, lastName)?.label ?? null;
